import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Navbar from './Navbar';
import Footer from './Footer';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import {
  Chart as ChartJS,
  ArcElement,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend
} from 'chart.js';
import { Doughnut, Bar } from 'react-chartjs-2';
import './AIDemo.css';

// Register Chart.js components
ChartJS.register(
  ArcElement,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend
);

const AIDemo = () => {
  const [status, setStatus] = useState('idle'); // idle | uploading | reading | extracting | analyzing | complete | error
  const [progress, setProgress] = useState(0);
  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [detailLevel, setDetailLevel] = useState('standard'); // brief | standard | deep
  const resultsRef = useRef(null);

  const detailOptions = [
    { key: 'brief', label: 'Executive Brief', desc: 'High-level verdict for board review' },
    { key: 'standard', label: 'Standard', desc: 'Full analysis for loan officers' },
    { key: 'deep', label: 'Deep Dive', desc: 'Comprehensive credit memo for underwriters' },
  ];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Scroll to results when complete
  useEffect(() => {
    if (status === 'complete' && resultsRef.current) {
      setTimeout(() => {
        resultsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 300);
    }
  }, [status]);

  const progressSteps = [
    { key: 'uploading', label: 'Uploading', icon: '📤' },
    { key: 'reading', label: 'Reading PDF', icon: '📄' },
    { key: 'extracting', label: 'Extracting Data', icon: '🔢' },
    { key: 'analyzing', label: 'AI Analysis', icon: '🧠' },
  ];

  const getStepStatus = useCallback((stepKey) => {
    const order = ['uploading', 'reading', 'extracting', 'analyzing'];
    const currentIndex = order.indexOf(status);
    const stepIndex = order.indexOf(stepKey);
    if (stepIndex < currentIndex) return 'completed';
    if (stepIndex === currentIndex) return 'active';
    return 'pending';
  }, [status]);

  const handleUseSample = async () => {
    setStatus('uploading');
    setProgress(10);
    setResults(null);
    setError('');

    try {
      const progressTimers = [];
      progressTimers.push(setTimeout(() => { setStatus('reading'); setProgress(30); }, 1000));
      progressTimers.push(setTimeout(() => { setStatus('extracting'); setProgress(55); }, 2500));
      progressTimers.push(setTimeout(() => { setStatus('analyzing'); setProgress(75); }, 4000));

      const response = await fetch('/.netlify/functions/analyze-financial', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ useSample: true, detailLevel }),
      });

      progressTimers.forEach(clearTimeout);

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.error || 'Analysis failed. Please try again.');
      }

      const data = await response.json();

      // Sanitize financials — guard against AI returning wrong values in fields
      // Allow $ amounts, % ratios, plain numbers (e.g., "1.73" for Current Ratio), and "205 days"
      const isValidValue = (v) => {
        if (!v) return false;
        if (v.startsWith('$') || v.endsWith('%') || v === 'N/A') return true;
        if (/^\d/.test(v)) return true; // starts with digit (covers "1.73", "205 days", etc.)
        return false;
      };
      if (data.financials) {
        data.financials = data.financials.map(row => ({
          ...row,
          currentYear: isValidValue(row.currentYear) ? row.currentYear : '—',
          priorYear: isValidValue(row.priorYear) ? row.priorYear : '—',
          change: (row.change && row.change.includes('%')) ? row.change : '—',
        }));
      }

      setResults(data);
      setStatus('complete');
      setProgress(100);
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
      setStatus('error');
      setProgress(0);
    }
  };

  const handleReset = () => {
    setStatus('idle');
    setProgress(0);
    setResults(null);
    setError('');
    setCopied(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCopyResults = () => {
    if (!results) return;

    let text = 'FINANCIAL ANALYSIS REPORT\n';
    text += 'Generated by Clarity Impact Finance AI\n';
    text += `${new Date().toLocaleDateString()}\n\n`;

    text += 'EXECUTIVE SUMMARY\n';
    results.summary.forEach(point => { text += `  \u2022 ${point}\n`; });

    text += '\nFINANCIAL HIGHLIGHTS\n';
    results.financials.forEach(row => {
      text += `  ${row.metric}: ${row.currentYear} (Prior: ${row.priorYear}) | Change: ${row.change}\n`;
    });

    text += '\nCDFI UNDERWRITER ANALYSIS\n';
    results.analysis.forEach(item => {
      text += `\n  [${item.severity.toUpperCase()}] ${item.title}\n`;
      text += `  ${item.content}\n`;
    });

    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    });
  };

  const handleDownloadPDF = () => {
    if (!results) return;

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();

    // Header bar
    doc.setFillColor(27, 70, 32);
    doc.rect(0, 0, pageWidth, 35, 'F');
    doc.setFontSize(20);
    doc.setTextColor(255, 255, 255);
    doc.text('Financial Analysis Report', 20, 18);
    doc.setFontSize(10);
    doc.setTextColor(200, 200, 200);
    doc.text('Generated by Clarity Impact Finance AI', 20, 26);
    doc.text(new Date().toLocaleDateString(), pageWidth - 20, 26, { align: 'right' });

    // Executive Summary
    let y = 48;
    doc.setFontSize(14);
    doc.setTextColor(27, 70, 32);
    doc.text('Executive Summary', 20, y);
    y += 3;
    doc.setDrawColor(230, 126, 69);
    doc.setLineWidth(1);
    doc.line(20, y, 80, y);
    y += 8;

    doc.setFontSize(10);
    doc.setTextColor(45, 55, 72);
    results.summary.forEach(point => {
      const lines = doc.splitTextToSize('\u2022 ' + point, pageWidth - 45);
      if (y + lines.length * 5 > 275) { doc.addPage(); y = 20; }
      doc.text(lines, 25, y);
      y += lines.length * 5 + 3;
    });

    // Financial Table
    y += 8;
    if (y > 240) { doc.addPage(); y = 20; }
    doc.setFontSize(14);
    doc.setTextColor(27, 70, 32);
    doc.text('Financial Highlights', 20, y);
    y += 3;
    doc.setDrawColor(230, 126, 69);
    doc.line(20, y, 80, y);
    y += 6;

    doc.autoTable({
      startY: y,
      head: [['Metric', 'Current Year', 'Prior Year', 'Change']],
      body: results.financials.map(row => [
        row.metric, row.currentYear, row.priorYear, row.change
      ]),
      styles: { fontSize: 9, cellPadding: 4 },
      headStyles: { fillColor: [27, 70, 32], textColor: [255, 255, 255], fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [248, 249, 250] },
      columnStyles: {
        0: { cellWidth: 60 },
        3: { fontStyle: 'bold' }
      }
    });

    // Analysis
    y = doc.lastAutoTable.finalY + 14;
    if (y > 240) { doc.addPage(); y = 20; }
    doc.setFontSize(14);
    doc.setTextColor(27, 70, 32);
    doc.text('CDFI Underwriter Analysis', 20, y);
    y += 3;
    doc.setDrawColor(230, 126, 69);
    doc.line(20, y, 80, y);
    y += 10;

    results.analysis.forEach(item => {
      if (y > 260) { doc.addPage(); y = 20; }

      const severityColors = {
        info: [27, 70, 32],
        warning: [230, 126, 69],
        concern: [229, 62, 62]
      };
      const color = severityColors[item.severity] || severityColors.info;
      doc.setFillColor(...color);
      doc.rect(20, y - 3, 3, 20, 'F');

      doc.setFontSize(11);
      doc.setTextColor(...color);
      doc.setFont(undefined, 'bold');
      doc.text(item.title, 28, y);
      y += 6;

      doc.setFontSize(9.5);
      doc.setTextColor(45, 55, 72);
      doc.setFont(undefined, 'normal');
      const lines = doc.splitTextToSize(item.content, pageWidth - 52);
      doc.text(lines, 28, y);
      y += lines.length * 4.5 + 10;
    });

    // Footer
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);
      doc.text('Clarity Impact Finance | clarityimpactfinance.com', 20, 288);
      doc.text(`Page ${i} of ${pageCount}`, pageWidth - 20, 288, { align: 'right' });
    }

    doc.save('financial-analysis-clarity.pdf');
  };

  // ─── Chart rendering helpers ───
  const chartColors = {
    green: '#1B4620',
    greenLight: 'rgba(27, 70, 32, 0.7)',
    orange: '#E67E45',
    orangeLight: 'rgba(230, 126, 69, 0.7)',
    red: '#e53e3e',
    teal: '#2d9f83',
    blue: '#3182ce',
    purple: '#805ad5',
    gray: '#718096',
    yellowGreen: '#68a844',
    palette: ['#1B4620', '#E67E45', '#2d9f83', '#3182ce', '#805ad5', '#e53e3e', '#68a844', '#718096', '#d69e2e']
  };

  const renderRevenueDoughnut = (chartData) => {
    if (!chartData?.revenueBreakdown?.length) return null;
    const data = {
      labels: chartData.revenueBreakdown.map(d => d.label),
      datasets: [{
        data: chartData.revenueBreakdown.map(d => d.value),
        backgroundColor: chartColors.palette.slice(0, chartData.revenueBreakdown.length),
        borderColor: '#fff',
        borderWidth: 2,
        hoverOffset: 8
      }]
    };
    return (
      <div className="chart-card">
        <h4>Revenue Breakdown</h4>
        <div className="chart-container chart-doughnut">
          <Doughnut data={data} options={{
            responsive: true,
            maintainAspectRatio: false,
            cutout: '55%',
            plugins: {
              legend: {
                position: 'bottom',
                labels: { padding: 12, usePointStyle: true, pointStyleWidth: 10, font: { size: 11 } }
              },
              tooltip: {
                callbacks: {
                  label: (ctx) => {
                    const total = ctx.dataset.data.reduce((a, b) => a + b, 0);
                    const pct = ((ctx.raw / total) * 100).toFixed(1);
                    return ` ${ctx.label}: $${ctx.raw.toLocaleString()} (${pct}%)`;
                  }
                }
              }
            }
          }} />
        </div>
      </div>
    );
  };

  const renderExpenseDoughnut = (chartData) => {
    if (!chartData?.expenseBreakdown?.length) return null;
    const data = {
      labels: chartData.expenseBreakdown.map(d => d.label),
      datasets: [{
        data: chartData.expenseBreakdown.map(d => d.value),
        backgroundColor: [chartColors.green, chartColors.orange, chartColors.teal],
        borderColor: '#fff',
        borderWidth: 2,
        hoverOffset: 8
      }]
    };
    return (
      <div className="chart-card">
        <h4>Expense Allocation</h4>
        <div className="chart-container chart-doughnut">
          <Doughnut data={data} options={{
            responsive: true,
            maintainAspectRatio: false,
            cutout: '55%',
            plugins: {
              legend: {
                position: 'bottom',
                labels: { padding: 12, usePointStyle: true, pointStyleWidth: 10, font: { size: 11 } }
              },
              tooltip: {
                callbacks: {
                  label: (ctx) => {
                    const total = ctx.dataset.data.reduce((a, b) => a + b, 0);
                    const pct = ((ctx.raw / total) * 100).toFixed(1);
                    return ` ${ctx.label}: $${ctx.raw.toLocaleString()} (${pct}%)`;
                  }
                }
              }
            }
          }} />
        </div>
      </div>
    );
  };

  const renderYoYBarChart = (chartData) => {
    if (!chartData?.yearOverYear?.length) return null;
    const data = {
      labels: chartData.yearOverYear.map(d => d.metric),
      datasets: [
        {
          label: 'Current Year',
          data: chartData.yearOverYear.map(d => d.currentYear),
          backgroundColor: chartColors.green,
          borderRadius: 4,
          barPercentage: 0.7
        },
        {
          label: 'Prior Year',
          data: chartData.yearOverYear.map(d => d.priorYear),
          backgroundColor: chartColors.orangeLight,
          borderRadius: 4,
          barPercentage: 0.7
        }
      ]
    };
    return (
      <div className="chart-card chart-card-wide">
        <h4>Year-over-Year Comparison</h4>
        <div className="chart-container chart-bar">
          <Bar data={data} options={{
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: {
                position: 'top',
                labels: { padding: 16, usePointStyle: true, pointStyleWidth: 10, font: { size: 11 } }
              },
              tooltip: {
                callbacks: {
                  label: (ctx) => ` ${ctx.dataset.label}: $${ctx.raw.toLocaleString()}`
                }
              }
            },
            scales: {
              y: {
                beginAtZero: true,
                ticks: {
                  callback: (val) => val >= 1000000 ? `$${(val / 1000000).toFixed(1)}M` : `$${(val / 1000).toFixed(0)}K`
                },
                grid: { color: 'rgba(0,0,0,0.06)' }
              },
              x: {
                ticks: { font: { size: 10 }, maxRotation: 30 },
                grid: { display: false }
              }
            }
          }} />
        </div>
      </div>
    );
  };

  const isProcessing = ['uploading', 'reading', 'extracting', 'analyzing'].includes(status);

  return (
    <div className="ai-demo-page">
      <Navbar />

      <main className="ai-demo-main">
        <div className="ai-demo-container">
          {/* Back link */}
          <Link to="/" className="ai-demo-back">&larr; Home</Link>

          {/* Header */}
          <div className="ai-demo-header">
            <div className="ai-demo-badge">AI-Powered Analysis</div>
            <h1>See AI-powered financial<br />analysis in action.</h1>
            <p>Watch how AI extracts, organizes, and analyzes nonprofit financials through the lens of a CDFI underwriter — using a real audited financial statement.</p>
          </div>

          {/* Sample Demo (show when idle or error) */}
          {(status === 'idle' || status === 'error') && (
            <div className="ai-demo-upload-section">
              {/* Detail level toggle */}
              <div className="detail-toggle">
                <span className="detail-toggle-label">Analysis depth</span>
                <div className="detail-toggle-options">
                  {detailOptions.map(opt => (
                    <button
                      key={opt.key}
                      className={`detail-toggle-btn ${detailLevel === opt.key ? 'active' : ''}`}
                      onClick={() => setDetailLevel(opt.key)}
                      title={opt.desc}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
                <span className="detail-toggle-desc">
                  {detailOptions.find(o => o.key === detailLevel)?.desc}
                </span>
              </div>

              <p className="ai-demo-cta-hint">Click below to start the analysis</p>
              <button className="sample-button sample-button-prominent" onClick={handleUseSample}>
                <span className="sample-button-glow" />
                <span className="sample-icon">📊</span>
                <span className="sample-button-label">Analyze Ossining Children's Center Audit</span>
                <span className="sample-subtitle">Real FY2022 audited financial statements (publicly available)</span>
                <span className="sample-button-arrow">→</span>
              </button>

              <div className="sample-context">
                <div className="sample-context-icon">🏫</div>
                <div className="sample-context-text">
                  <strong>About the sample:</strong> Ossining Children's Center is a 501(c)(3) nonprofit in Ossining, NY,
                  founded in 1906. They provide early childhood education, before/after-school care, and summer camp programs.
                  They recently completed a new school building (the Rebecca and Arthur Samberg Building) and serve children ages 8 weeks through school-age.
                </div>
              </div>

              <a
                href="https://book.morgen.so/aali47/c87v4y"
                target="_blank"
                rel="noopener noreferrer"
                className="consult-cta"
              >
                <span className="consult-cta-icon">📅</span>
                <span className="consult-cta-text">
                  <strong>Book a 30-Min AI Consultation</strong>
                  <span>See how AI analysis can transform your underwriting workflow</span>
                </span>
                <span className="consult-cta-arrow">→</span>
              </a>

              {error && (
                <div className="error-message">
                  <span className="error-icon">⚠️</span>
                  {error}
                  <button onClick={() => setError('')} className="error-dismiss">&times;</button>
                </div>
              )}
            </div>
          )}

          {/* Processing State */}
          {isProcessing && (
            <div className="processing-section">
              <div className="processing-card">
                <div className="processing-file">
                  <span className="file-icon">🏫</span>
                  <span className="file-name">Ossining Children's Center - FY2022 Audit</span>
                  <span className="file-pages">47 pages</span>
                </div>

                <div className="progress-bar-track">
                  <div className="progress-bar-fill" style={{ width: `${progress}%` }} />
                </div>

                <div className="progress-steps">
                  {progressSteps.map(step => (
                    <div key={step.key} className={`progress-step ${getStepStatus(step.key)}`}>
                      <span className="step-icon">{step.icon}</span>
                      <span className="step-label">{step.label}</span>
                    </div>
                  ))}
                </div>

                <div className="processing-status-line">
                  <span className="status-cursor" />
                  {status === 'uploading' && 'Connecting to AI engine...'}
                  {status === 'reading' && 'Reading 47 pages of audited financials...'}
                  {status === 'extracting' && 'Extracting financial data from balance sheets, income statements, and notes...'}
                  {status === 'analyzing' && 'Running CDFI underwriter analysis on extracted data...'}
                </div>
              </div>
            </div>
          )}

          {/* Results */}
          {status === 'complete' && results && (
            <div className="results-section" ref={resultsRef}>
              {/* Summary */}
              <div className="result-card result-summary">
                <div className="result-card-header">
                  <span className="result-card-icon">📋</span>
                  <h3>Executive Summary</h3>
                </div>
                <ul className="summary-list">
                  {results.summary.map((point, i) => (
                    <li key={i}>{point}</li>
                  ))}
                </ul>
              </div>

              {/* Charts Section */}
              {results.charts && (
                <div className="charts-section">
                  <div className="result-card-header charts-header">
                    <span className="result-card-icon">📈</span>
                    <h3>Visual Analysis</h3>
                  </div>
                  <div className="charts-grid">
                    {renderRevenueDoughnut(results.charts)}
                    {renderExpenseDoughnut(results.charts)}
                    {renderYoYBarChart(results.charts)}
                  </div>
                </div>
              )}

              {/* Financial Table */}
              <div className="result-card result-financials">
                <div className="result-card-header">
                  <span className="result-card-icon">📊</span>
                  <h3>Financial Highlights</h3>
                </div>
                <div className="table-wrapper">
                  <table className="financial-table">
                    <thead>
                      <tr>
                        <th>Metric</th>
                        <th>Current Year</th>
                        <th>Prior Year</th>
                        <th>Change</th>
                      </tr>
                    </thead>
                    <tbody>
                      {results.financials.map((row, i) => (
                        <tr key={i}>
                          <td className="metric-name">{row.metric}</td>
                          <td className="metric-value">{row.currentYear}</td>
                          <td className="metric-value">{row.priorYear}</td>
                          <td className={`metric-change ${row.changeDirection === 'positive' ? 'change-positive' : row.changeDirection === 'negative' ? 'change-negative' : ''}`}>
                            {row.change}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Analysis */}
              <div className="result-card result-analysis">
                <div className="result-card-header">
                  <span className="result-card-icon">🔍</span>
                  <h3>CDFI Underwriter Analysis</h3>
                </div>
                <div className="analysis-items">
                  {results.analysis.map((item, i) => (
                    <div key={i} className={`analysis-item severity-${item.severity}`}>
                      <div className="analysis-item-header">
                        <span className={`severity-badge ${item.severity}`}>
                          {item.severity === 'info' ? '✓' : item.severity === 'warning' ? '⚡' : '⚠'}
                          {' '}{item.severity}
                        </span>
                        <h4>{item.title}</h4>
                      </div>
                      <p>{item.content}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="result-actions">
                <button className="action-btn action-download" onClick={handleDownloadPDF}>
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M9 2v10" />
                    <path d="M5 8l4 4 4-4" />
                    <path d="M2 14h14" />
                  </svg>
                  Download PDF
                </button>
                <button className="action-btn action-copy" onClick={handleCopyResults}>
                  {copied ? '✓ Copied!' : '📋 Copy to Clipboard'}
                </button>
                <button className="action-btn action-reset" onClick={handleReset}>
                  ↻ Analyze Another
                </button>
              </div>

              {/* Consultation CTA */}
              <a
                href="https://book.morgen.so/aali47/c87v4y"
                target="_blank"
                rel="noopener noreferrer"
                className="consult-cta consult-cta-results"
              >
                <span className="consult-cta-icon">📅</span>
                <span className="consult-cta-text">
                  <strong>Book a 30-Min AI Consultation</strong>
                  <span>Discuss how Clarity can build custom AI analysis for your CDFI</span>
                </span>
                <span className="consult-cta-arrow">→</span>
              </a>

              {/* Disclaimer */}
              <p className="results-disclaimer">
                This analysis is generated by AI and is intended for informational purposes only.
                All outputs should be reviewed by qualified professionals before use in lending decisions.
              </p>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default AIDemo;
